import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { BackLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { Table } from '~/components/ui/Table';
import styles from '~/styles/button.module.css';
import { CreateTopicButton } from './CreateTopicButton';

type Topic = components['schemas']['TopicAdminSummary'];

export const Route = createFileRoute('/_admin/topics/')({
  validateSearch: (s: Record<string, unknown>) => ({
    lesson: Number(s.lesson),
  }),
  component: TopicsList,
});

const columnHelper = createColumnHelper<Topic>();

const columns = [
  columnHelper.accessor('order', { header: 'Order' }),
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('status', { header: 'Status' }),
];

function TopicsList() {
  const { lesson } = Route.useSearch();
  const [page, setPage] = useState(0);

  // Admin endpoint: all statuses (the student lesson_topics is gated to published).
  const { data, isLoading, error } = api.adminListTopics.useQuery({
    path: { lesson_id: lesson },
    query: { limit: 10, offset: 10 * page },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Message>Loading topics...</Message>;
  if (error) return <Message $error>Error loading topics</Message>;

  return (
    <div>
      <BackLink>
        <Link to="/lessons/$lessonId" params={{ lessonId: String(lesson) }}>
          &larr; Back to lesson
        </Link>
      </BackLink>
      <Header>
        <PageTitle>Topics</PageTitle>
        <CreateTopicButton lessonId={lesson} />
      </Header>
      <Toolbar>
        <div style={{ flexGrow: 1 }}>Page {page + 1}</div>
        <Button
          disabled={page === 0}
          className={styles.Button}
          onClick={() => setPage((v) => v - 1)}
        >
          Previous
        </Button>
        <Button className={styles.Button} onClick={() => setPage((v) => v + 1)}>
          Next
        </Button>
      </Toolbar>

      <Table
        table={table}
        to="/topics/$topicId"
        getParams={(row) => ({ topicId: String(row.id) })}
      />
    </div>
  );
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--component-margin);
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  margin: var(--component-margin);
  gap: var(--component-margin);
`;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
